import {
  deprecatedServices, renamedServices, previewServices,
  securityHubCspmSignals, featureComparisons, techEditTerms,
  supersededServices, supersededReplacements,
} from '../config/aws-service-data';

/**
 * Scan LLM output text for service-related issues.
 * Returns array of {type, severity, message, service?}
 */
export function scanOutputForIssues(text, options = {}) {
  if (!text) return [];
  const findings = [];
  const lower = text.toLowerCase();

  // 1.1 Deprecated services
  for (const svc of deprecatedServices) {
    const svcLower = svc.toLowerCase();
    if (!lower.includes(svcLower)) continue;
    // Skip bare "Usage Report" unless preceded by AWS/Amazon
    if (svcLower === 'usage report') {
      if (!/\b(?:aws|amazon)\s+usage\s+reports?\b/i.test(text)) continue;
    }
    // Skip "Cost and Usage Report" false positive
    if (svcLower.includes('usage report') && lower.includes('cost and usage report')) {
      const cleaned = lower.replace(/cost and usage report/g, '');
      if (!cleaned.includes(svcLower)) continue;
    }
    findings.push({ type: 'deprecated', severity: 'warning', message: `References deprecated service: ${svc}`, service: svc });
    break; // one finding per deprecated match is enough
  }

  // 1.1b Superseded services — officially announced replacement, still
  // functional today but AWS recommends migration. Same policy severity as
  // deprecated (item risks going stale mid-lifecycle). Surfaces the
  // replacement hint when available.
  for (const svc of supersededServices) {
    const svcLower = svc.toLowerCase();
    if (!lower.includes(svcLower)) continue;
    const hint = supersededReplacements[svc];
    findings.push({
      type: 'superseded',
      severity: 'warning',
      message: hint
        ? `References superseded service: ${svc} — replacement: ${hint.replacement}`
        : `References superseded service: ${svc}`,
      service: svc,
    });
    break; // one finding per superseded match is enough
  }

  // 1.2 Renamed services
  for (const [oldName, newName] of Object.entries(renamedServices)) {
    const oldLower = oldName.toLowerCase();
    const newLower = newName.toLowerCase();
    let check = lower.replace(new RegExp(newLower.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), '');
    if (check.includes(oldLower)) {
      findings.push({ type: 'renamed', severity: 'warning', message: `Uses old name "${oldName}" → should be "${newName}"`, service: oldName });
    }
  }

  // 1.3 Preview/non-GA services
  for (const svc of previewServices) {
    if (lower.includes(svc.toLowerCase())) {
      findings.push({ type: 'preview', severity: 'warning', message: `References preview/non-GA service: ${svc}`, service: svc });
      break;
    }
  }

  // 1.4 Security Hub CSPM
  if (lower.includes('security hub') && !lower.includes('security hub cspm')) {
    if (securityHubCspmSignals.some(sig => lower.includes(sig))) {
      findings.push({ type: 'cspm', severity: 'warning', message: 'Uses "Security Hub" in CSPM context — should be "Security Hub CSPM"' });
    }
  }

  // 1.5 Missing link per rationale section
  if (options.checkMissingLinks) {
    const sections = text.split(/\*\*[A-F]\b/);
    if (sections.length > 1) {
      for (let i = 1; i < sections.length; i++) {
        const letter = String.fromCharCode(64 + i);
        if (!/https?:\/\//.test(sections[i])) {
          findings.push({ type: 'missing-link', severity: 'warning', message: `Rationale ${letter} has no documentation link` });
        }
      }
    }
  }

  // 1.6 Console references
  if (/\b(?:aws\s+(?:management\s+)?console|management\s+console|navigate\s+to\s+the\s+console)\b/i.test(text)) {
    findings.push({ type: 'console-ref', severity: 'warning', message: 'References AWS Management Console — items should test CLI/API/IaC knowledge' });
  }

  // 1.7 Teaching language
  const teachingPatterns = [
    /\bthis ensures\b/i, /\bfor example\b/i, /\bthis means\b/i,
    /\bin other words\b/i, /\bthis is because\b/i,
  ];
  for (const pat of teachingPatterns) {
    if (pat.test(text)) {
      findings.push({ type: 'teaching', severity: 'info', message: `Contains teaching language: "${text.match(pat)[0]}"` });
      break;
    }
  }

  // 1.8 ESO tech-edit terminology scenarios — ported from AgenticItemReviewer
  // config/eso/tech_edit_scenarios.json (ESO IA canonical). Flags style/naming/
  // spelling terms that TE would flag during copy edit, with the same
  // hover_text guidance shown in the source tool.
  for (const [term, meta] of Object.entries(techEditTerms)) {
    const found = meta.case_sensitive
      ? text.includes(term)
      : lower.includes(term.toLowerCase());
    if (found) {
      findings.push({
        type: 'tech-edit',
        severity: 'info',
        message: `Terminology: "${term}" — ${meta.hover_text}`,
        service: term,
      });
    }
  }

  return findings;
}

/**
 * Get feature comparison matrices matching the item text.
 */
export function getMatchingComparisons(text) {
  if (!text) return [];
  const lower = text.toLowerCase();
  return Object.entries(featureComparisons)
    .filter(([, v]) => v.trigger_terms.some(t => lower.includes(t)))
    .map(([key, v]) => ({ key, ...v }));
}
